// Run scan for one account, as the admission policy answers it (#1274). The server decides
// whether a scan may start (`GET /api/v1/posture/scans/availability`); this file words the
// reason code it gives and decides nothing. The same control sits on Posture › Score and on
// each account's card on the AWS accounts page.
//
// A refused account keeps its control: a button marked `aria-disabled` stays in the Tab order,
// so a keyboard or screen-reader user reaches it and hears why, and the reason is text on the
// page that the button is described by. Never a `title`: a phone and a keyboard reach neither.

import { useId, useState } from "react";

import { ApiError, messageOf } from "../api/http";
import type { ScanAvailability, ScanRefusal } from "../api/posture";
import { readableTime } from "./runPresentation";

const either = (words: string[]): string =>
  words.length < 2 ? words.join("") : `${words.slice(0, -1).join(", ")} or ${words[words.length - 1]}`;

/**
 * The sentence for each reason code. Typed over `ScanRefusal`, so a code added there without
 * words here does not compile.
 */
const REFUSAL_WORDS: Record<ScanRefusal, (account: ScanAvailability, scanRoles: string[]) => string> = {
  no_permission: (_account, roles) =>
    roles.length === 0
      ? "Your role cannot start a scan. Ask the workspace's owner."
      : `Starting a scan needs the ${either(roles)} role. Ask someone in this workspace who has it.`,
  grant_lacks_scan: () =>
    "This workspace has not given your Partner access the scan permission. The workspace's owner can allow it.",
  not_shared_for_scan: () =>
    "This account is not shared with you for scanning. The workspace's owner can share it.",
  batch_running: () => "A graded batch is running on dev, so no scan starts until it ends.",
  batch_marker_unreadable: () =>
    "Whether a graded batch is running on dev could not be read, so no scan starts. Try again shortly.",
  scanner_not_configured: () => "The scanner is not set up here, so no scan can start.",
  account_not_found: () => "This account is not in this workspace, so it cannot be scanned.",
  organization_member: () =>
    "This account was added from an AWS Organization, and member accounts of an AWS Organization cannot be scanned yet.",
  role_not_scannable: () =>
    "This account's role is not the read-only role a scan uses, so it cannot be scanned. Connect it again with the stack Connect account gives you.",
  scan_active: () => "A scan of this account is already running.",
  account_not_active: () => "This account is not connected, so it cannot be scanned.",
  too_soon: (account) =>
    account.next_allowed_at === null
      ? "This account was scanned less than an hour ago."
      : `This account was scanned less than an hour ago. Next scan allowed at ${readableTime(account.next_allowed_at)}.`,
};

/** Why a scan may not start on this account, in words; null when it may. */
export function scanRefusalWords(account: ScanAvailability, scanRoles: string[]): string | null {
  if (account.may_scan || account.reason === null) return null;
  const words = REFUSAL_WORDS[account.reason];
  return words === undefined ? "A scan cannot start on this account." : words(account, scanRoles);
}

/** When the account last finished a scan, in words. */
export const lastScannedWords = (account: ScanAvailability): string =>
  account.last_scan_at === null ? "Never scanned" : `Last scanned ${readableTime(account.last_scan_at)}`;

/** Run scan for one account: pressed when the server admits it, and otherwise why not. */
export function ScanControl({
  account,
  scanRoles,
  onRun,
}: {
  account: ScanAvailability;
  scanRoles: string[];
  onRun: (cloudAccountId: string) => Promise<void>;
}) {
  const reasonId = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refused = scanRefusalWords(account, scanRoles);
  const run = () => {
    if (refused !== null || busy) return;
    setBusy(true);
    setError(null);
    onRun(account.cloud_account_id).then(
      () => setBusy(false),
      (e: unknown) => {
        setBusy(false);
        setError(
          e instanceof ApiError && e.status === 403 ? "You don't have permission to run a scan." : messageOf(e),
        );
      },
    );
  };
  return (
    <div className="scan-control">
      <button
        type="button"
        className="btn btn--small"
        aria-disabled={refused !== null || busy}
        aria-describedby={refused === null ? undefined : reasonId}
        onClick={run}
      >
        Run scan<span className="sr-only"> of {account.name ?? account.account_id}</span>
      </button>
      {refused === null ? null : (
        <p className="muted" id={reasonId}>
          {refused}
        </p>
      )}
      {error === null ? null : (
        <p className="muted" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
